import type { CreditSpreadChainRow, OptionRight, StartCreditSpreadBacktestRequest } from '@fleece/models';

/** Two legs of one expiration, ordered: the short is the one nearer the money. */
export interface PickedSpread {
  readonly short: CreditSpreadChainRow;
  readonly long: CreditSpreadChainRow;
}

/**
 * Toggles a contract in the pick. At most two, both from one expiration: picking from
 * another expiration starts over with that contract, and a third pick replaces the
 * older of the two — the one picked earlier is the one least likely to still be meant.
 */
export function togglePick(picked: ReadonlyArray<CreditSpreadChainRow>, row: CreditSpreadChainRow): ReadonlyArray<CreditSpreadChainRow> {
  if (picked.some((p) => p.symbol === row.symbol)) {
    return picked.filter((p) => p.symbol !== row.symbol);
  }
  if (picked.some((p) => p.expiration !== row.expiration)) {
    return [row];
  }
  return picked.length < 2 ? [...picked, row] : [picked[1], row];
}

export function toSpread(picked: ReadonlyArray<CreditSpreadChainRow>): PickedSpread | undefined {
  if (picked.length !== 2) {
    return undefined;
  }
  const [a, b] = picked;
  return a.moneyness <= b.moneyness ? { short: a, long: b } : { short: b, long: a };
}

export interface SpreadFigures {
  readonly width: number;
  /** Selling the short at its bid, buying the long at its ask. */
  readonly naturalCredit?: number;
  readonly midCredit?: number;
  /** Width less the natural credit: what one share of the spread can lose. */
  readonly maxLoss?: number;
  /** Natural credit over width. */
  readonly creditToWidth?: number;
  readonly breakeven?: number;
}

export function spreadFigures(spread: PickedSpread, right: OptionRight): SpreadFigures {
  const width = Math.abs(spread.short.strike - spread.long.strike);
  const naturalCredit = spread.short.bid !== undefined && spread.long.ask !== undefined ? spread.short.bid - spread.long.ask : undefined;
  const midCredit = spread.short.mid !== undefined && spread.long.mid !== undefined ? spread.short.mid - spread.long.mid : undefined;
  return {
    width,
    naturalCredit,
    midCredit,
    maxLoss: naturalCredit === undefined ? undefined : width - naturalCredit,
    creditToWidth: naturalCredit === undefined ? undefined : naturalCredit / width,
    breakeven: naturalCredit === undefined ? undefined : right === 'put' ? spread.short.strike - naturalCredit : spread.short.strike + naturalCredit,
  };
}

/** What the backtest form edits: percentages as the reader types them, dates as `YYYY-MM-DD`. */
export interface BacktestForm {
  readonly takeProfitPct: number;
  readonly stopLossPct: number;
  readonly dteMin: number;
  readonly dteMax: number;
  readonly moneynessMinPct: number;
  readonly moneynessMaxPct: number;
  readonly entryTime: string;
  readonly shortDayEntryTime: string;
  readonly from: string;
  readonly to: string;
  readonly avoidEarnings: boolean;
}

/** Alpaca's option history starts here, and the service refuses anything earlier. */
export const OPTION_HISTORY_START = '2024-02-01';

function shiftDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function round(value: number, digits: number): number {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

/**
 * A starting point around the picked spread: its DTE ± 5 days, its moneyness ± 1 point,
 * the last year of sessions ending yesterday, and noon entries (11:00 on a half day,
 * which closes at 13:00).
 */
export function defaultBacktestForm(spread: PickedSpread, today: string): BacktestForm {
  const dte = spread.short.dte;
  const moneyness = spread.short.moneyness * 100;
  const yearAgo = shiftDays(today, -365);
  return {
    takeProfitPct: 60,
    stopLossPct: 200,
    dteMin: Math.max(1, dte - 5),
    dteMax: dte + 5,
    moneynessMinPct: Math.max(0, round(moneyness - 1, 1)),
    moneynessMaxPct: round(moneyness + 1, 1),
    entryTime: '12:00',
    shortDayEntryTime: '11:00',
    from: yearAgo < OPTION_HISTORY_START ? OPTION_HISTORY_START : yearAgo,
    to: shiftDays(today, -1),
    avoidEarnings: false,
  };
}

export function toBacktestRequest(spread: PickedSpread, form: BacktestForm): StartCreditSpreadBacktestRequest {
  return {
    shortSymbol: spread.short.symbol,
    longSymbol: spread.long.symbol,
    takeProfit: form.takeProfitPct / 100,
    stopLoss: form.stopLossPct / 100,
    dteMin: form.dteMin,
    dteMax: form.dteMax,
    moneynessMin: form.moneynessMinPct / 100,
    moneynessMax: form.moneynessMaxPct / 100,
    entryTime: form.entryTime,
    shortDayEntryTime: form.shortDayEntryTime,
    from: form.from,
    to: form.to,
    avoidEarnings: form.avoidEarnings,
  };
}

/** The expiration a chain opens on: the one nearest 30 days, the middle of the 7–45 window. */
export function defaultExpiration(rows: ReadonlyArray<CreditSpreadChainRow>): string | undefined {
  let best: CreditSpreadChainRow | undefined = undefined;
  for (const row of rows) {
    if (best === undefined || Math.abs(row.dte - 30) < Math.abs(best.dte - 30)) {
      best = row;
    }
  }
  return best?.expiration;
}
